import ReactDOM from 'react-dom'
import React, { ReactNode } from 'react'
import { checkContainClassName, findElement, findElementList, getElementText } from '../../utils/element'
import SparkMD5 from '../../utils/md5'

// 标记的节点
export const markedRules = [
    {
        nodeName: 'A'
    },
    {
        nodeName: 'BUTTON',
        className: '!ant-dropdown-trigger'
    },
    {
        className: 'ant-dropdown-menu-item'
    },
    {
        className: 'ant-tabs-tab'
    },
    {
        className: 'ant-radio-button-wrapper'
    },
    {
        className: 'ant-radio-wrapper'
    }
]

const DIY_TD_CLASSNAME = 'antd-pro-components-table-widgets-virtual-table-index-td'
const DIY_EMPTY = 'antd-pro-components-antd-base-table-index-emptyWrap'

// 获取表格列头信息
export const getTableInfo = (element: HTMLElement, table: HTMLElement) => {
    const thead = table.querySelector('.ant-table-header,.art-table-header')?.querySelector('thead')
    const allTh = thead?.querySelectorAll('th')

    const obj = {
        element: 'table_click'
    }

    if (!allTh) {
        return {
            ...obj,
            title: ''
        }
    }

    const isTd = (node: HTMLElement): boolean => !!(node.nodeName && node.nodeName.toUpperCase() === 'TD')
    const isDiyTd = (node: HTMLElement): boolean => node.classList && node.classList.contains(DIY_TD_CLASSNAME)
    const isDiyEmpty = (node: HTMLElement): boolean => node.classList && node.classList.contains(DIY_EMPTY)

    const empty = findElement(element, (node: HTMLElement) => isDiyEmpty(node))

    if (empty) {
        return {
            ...obj,
            title: ''
        }
    }

    // td
    const td = findElement(element, (node: HTMLElement) => isTd(node) || isDiyTd(node))

    if (td) {
        const tdIndex = Array.from(
            (isTd(td) && td.parentNode.querySelectorAll('td')) ||
                (isDiyTd(td) && td.parentNode.querySelectorAll(`.${DIY_TD_CLASSNAME}`)) ||
                []
        ).findIndex(v => v === td)

        const title = getElementText(allTh[tdIndex])

        return {
            ...obj,
            title
        }
    }

    const th = findElement(element, (node: HTMLElement) => !!(node.nodeName && node.nodeName.toUpperCase() === 'TH'))

    if (th) {
        const title = getElementText(th)

        return {
            ...obj,
            title
        }
    }

    return {
        ...obj,
        title: ''
    }
}

// 获取抽屉信息
export const getDrawerInfo = (element: HTMLElement, drawer: HTMLElement) => {
    const drawerTitle = drawer.getAttribute('log-title')

    if (drawerTitle) {
        return {
            title: drawerTitle
        }
    }

    const titleEl = drawer.querySelector('[log-title]')

    if (titleEl) {
        const logTitle = titleEl.getAttribute('log-title')

        if (logTitle) {
            return {
                title: logTitle
            }
        }
    }

    const t: any = drawer.querySelector('.ant-drawer-title')

    const title = getElementText(t)

    return {
        title
    }
}

// 获取弹窗信息
export const getModalInfo = (element: HTMLElement, modal: HTMLElement) => {
    // 无法透传log-title属性到modal上
    // 查找内部含有log-title属性的元素，如：<div log-title="测试" />
    const titleEl = modal.querySelector('[log-title]')

    if (titleEl) {
        const logTitle = titleEl.getAttribute('log-title')

        if (logTitle) {
            return {
                title: logTitle
            }
        }
    }

    const t: any = modal.querySelector('.ant-modal-title')

    const title = getElementText(t)

    return {
        title
    }
}

// 获取表单信息
export const getFormInfo = (element: HTMLElement, node: HTMLElement) => {
    const t: any = node.querySelector('.ant-form-item-label')

    const title = getElementText(t)

    return {
        title
    }
}

// 获取tab信息
export const getTabInfo = (element: HTMLElement, node: HTMLElement) => {
    const tab = node?.parentNode?.parentNode?.parentNode?.querySelector('.ant-tabs-tab.ant-tabs-tab-active')

    const title = getElementText(tab as any)

    return {
        title
    }
}

// 分页器
export const PAGINATION_RULE = {
    className: ['ant-pagination'],
    info: {
        title: '分页器'
    }
}

// 下拉菜单
export const TOOL_RULE = {
    className: ['ant-dropdown-menu-item'],
    info: {
        title: '',
        element: 'tool_click'
    }
}

// 表格
export const TABLE_RULE = {
    className: ['ant-table', 'art-table'],
    info: getTableInfo
}

// 表单
export const FORM_RULE = {
    className: ['ant-form-item'],
    info: getFormInfo
}

// 选项卡
export const TAB_RULE = {
    className: ['ant-tabs-tabpane'],
    info: getTabInfo
}

// 模态窗
export const MODAL_RULE = {
    className: ['ant-modal'],
    info: getModalInfo
}

// 抽屉
export const DRAWER_RULE = {
    className: ['ant-drawer'],
    info: getDrawerInfo
}

// dataset
export const DATASET_RULE = {
    dataset: ['logText'],
    info(_: any, node: HTMLElement){
        return {
            title: node.dataset.logText
        }
    }
}

export const RELATED_INFO_RULES = [DATASET_RULE, PAGINATION_RULE, TOOL_RULE, TABLE_RULE, FORM_RULE, TAB_RULE, MODAL_RULE, DRAWER_RULE]

// 查找父节点特殊节点集合
export const resolveRelatedInfo = (element: HTMLElement, config = { rules: RELATED_INFO_RULES }) => {
    const { rules } = config

    return findElementList(element, (node: HTMLElement): any => {
        // dataset不取当前节点，避免text重复
        if(node?.dataset && node !== element){
            const match: any = rules.find((v: any) => v.dataset && Object.keys(node.dataset).some(v1 => v.dataset.includes(v1)))

            if (match) {
                return typeof match.info === 'function' ? match.info(element, node) : match.info
            }
        }

        if (node?.classList) {
            const match: any = rules.find((v: any) => v.className && checkContainClassName(node, v.className))

            if (match) {
                return typeof match.info === 'function' ? match.info(element, node) : match.info
            }
        }

        return null
    })
}

// 覆盖 ReactDOM.createPortal，根据title计算md5值作为id，解决弹窗埋点xpath问题
const rawCreatePortal = ReactDOM.createPortal

// 判断是否是弹窗
const MODAL_PREFIX_CLS = 'ant-modal'
const DRAWER_PREFIX_CLS = 'ant-drawer'

// 覆盖弹窗
export const overrideCreatePortal = (config: any) => {
    const { modalPrefixCls, drawPrefixCls } = config || {}

    // 标记是否已被覆盖
    if (ReactDOM._jst_overrided) {
        return
    }

    // 根据title构建id
    const genId = (title: string) => {
        return SparkMD5.hash(title)
    }

    // 检测是否是弹窗
    const checkIsModal = (component: any) => {
        return component?.props?.prefixCls && component?.props?.prefixCls.includes(modalPrefixCls || MODAL_PREFIX_CLS)
    }

    // 检测是否是抽屉
    const checkIsDrawer = (component: any) => {
        return component?.props?.prefixCls && component?.props?.prefixCls.includes(drawPrefixCls || DRAWER_PREFIX_CLS)
    }

    // 获取抽屉的title，比较特殊，无法从props直接获取到
    const getTitle: any = (component: any) => {
        if (!component) {
            return ''
        }

        if (typeof component === 'string') {
            return component
        }

        if (typeof component?.props?.logTitle === 'string') {
            return component.props.logTitle
        }

        if (component?.props?.title) {
            if (typeof component.props.title === 'string') {
                return component.props.title
            }

            if (React.isValidElement(component.props.title)) {
                return getTitle(component.props.title)
            }
        }

        if (Array.isArray(component.props.children)) {
            return getTitle(component.props.children[0])
        }

        return getTitle(component.props.children)
    }

    ReactDOM.createPortal = (component, mountEl): any => {
        // 弹窗
        if (checkIsModal(component)) {
            // 已有id
            if (component?.props?.id) {
                return rawCreatePortal(component, mountEl)
            }

            const title = getTitle(component)

            // title 不存在
            if (!title) {
                return rawCreatePortal(component, mountEl)
            }

            // @ts-ignore
            const newComponent = React.cloneElement(component, {
                modalRender(node: ReactNode) {
                    const rawModalRender = component?.props?.modalRender

                    const id = genId(title)

                    if (typeof rawModalRender === 'function') {
                        return <div id={id}>{rawModalRender(node)}</div>
                    }

                    return <div id={id}>{node}</div>
                }
            })

            return rawCreatePortal(newComponent, mountEl)
        }

        if (checkIsDrawer(component)) {
            // 已有id
            if (component?.props?.id) {
                return rawCreatePortal(component, mountEl)
            }

            const title = getTitle(component)

            // title 不存在
            if (!title) {
                return rawCreatePortal(component, mountEl)
            }

            // @ts-ignore
            const newComponent = React.cloneElement(component, {
                id: genId(title)
            })

            return rawCreatePortal(newComponent, mountEl)
        }

        return rawCreatePortal(component, mountEl)
    }

    ReactDOM._jst_overrided = true
}
